import React from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, CheckCircle2, Globe, MessageCircle, ScanLine, Calculator } from 'lucide-react';
import Seo from '../components/Seo';
import LandingHeader from '../components/LandingHeader';
import RelatedLinks from '../components/RelatedLinks';
import TaxCalculator from './TaxCalculator';
import TryScanner from '../components/TryScanner';
import { ES_PAGES, ES_LIST } from '../data/esPages';

const SITE = 'https://moneyfeed.io';

const KIND_ICON = { calculator: Calculator, whatsapp: MessageCircle, scanner: ScanLine, guide: Globe };

export default function EsLanding({ slug }) {
  const data = ES_PAGES[slug];
  if (!data) return <Navigate to="/" replace />;

  const path = `/es/${data.slug}`;
  const Icon = KIND_ICON[data.kind] || Globe;
  const others = ES_LIST.filter((p) => p.slug !== data.slug);

  const alternates = data.enHref
    ? [
        { hreflang: 'en', href: SITE + data.enHref },
        { hreflang: 'es-US', href: SITE + path },
        { hreflang: 'x-default', href: SITE + data.enHref },
      ]
    : [
        { hreflang: 'es-US', href: SITE + path },
        { hreflang: 'x-default', href: SITE + path },
      ];

  const jsonLd = [
    { '@context': 'https://schema.org', '@type': 'WebPage', name: data.h1, url: `${SITE}${path}`, description: data.seoDescription, inLanguage: 'es' },
    { '@context': 'https://schema.org', '@type': 'FAQPage', mainEntity: data.faqs.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })) },
    { '@context': 'https://schema.org', '@type': 'BreadcrumbList', itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'MoneyFeed', item: SITE + '/' },
      { '@type': 'ListItem', position: 2, name: data.h1, item: `${SITE}${path}` },
    ] },
  ];
  if (data.kind === 'scanner') {
    jsonLd.push({ '@context': 'https://schema.org', '@type': 'HowTo', name: data.h1, step: data.steps.map((s, i) => ({ '@type': 'HowToStep', position: i + 1, text: s })) });
  }

  return (
    <div dir="ltr" className="min-h-screen bg-gradient-to-br from-[#F8FAFC] via-[#ECFDF5] to-[#D1FAE5]" data-testid={`es-landing-${data.slug}`}>
      <Seo title={data.seoTitle} description={data.seoDescription} path={path} jsonLd={jsonLd} alternates={alternates} lang="es" dir="ltr" />
      <LandingHeader />

      <section className="max-w-4xl mx-auto px-4 sm:px-8 pt-14 pb-8 text-center">
        {data.enHref && (
          <div className="flex justify-center mb-4">
            <Link to={data.enHref} data-testid="es-lang-toggle" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#064E3B] hover:text-[#84CC16]">
              <Globe className="h-4 w-4" /> English
            </Link>
          </div>
        )}
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-5" style={{ backgroundColor: `${data.accent}1A` }}>
          <Icon className="h-4 w-4" style={{ color: data.accent }} />
          <span className="text-xs font-bold uppercase tracking-wider" style={{ color: data.accent }}>{data.flag} {data.badge}</span>
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#064E3B] leading-tight mb-5">{data.h1}</h1>
        <p className="text-base sm:text-lg text-gray-600 mb-4 max-w-2xl mx-auto">{data.intro}</p>
      </section>

      {(data.points || data.body) && (
        <section className="max-w-3xl mx-auto px-4 sm:px-8 pb-4">
          {data.points && (
            <div className="bg-white rounded-3xl shadow-md p-6 sm:p-8 border border-gray-100">
              <h2 className="text-xl font-bold text-[#064E3B] mb-4">{data.kind === 'guide' ? 'Lo que debes saber' : 'Las cifras clave (2026)'}</h2>
              <ul className="space-y-3">
                {data.points.map((p, i) => (
                  <li key={i} className="flex gap-3 text-gray-700" data-testid={`es-point-${i}`}>
                    <CheckCircle2 className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color: data.accent }} />
                    <span className="text-sm sm:text-base">{p}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {data.body && (
            <div className="mt-6 space-y-4">
              {data.body.map((p, i) => (
                <p key={i} className="text-gray-700 text-sm sm:text-base" data-testid={`es-body-${i}`}>{p}</p>
              ))}
            </div>
          )}
        </section>
      )}

      {data.kind === 'calculator' && (
        <section className="max-w-5xl mx-auto px-4 sm:px-8 py-8">
          <div className="bg-white rounded-3xl shadow-md p-4 sm:p-6 border border-gray-100">
            <h2 className="text-2xl font-bold text-[#064E3B] text-center mb-1">Calcula tu estimado</h2>
            <p className="text-sm text-gray-500 text-center mb-5">Ingresa tu ingreso y tus gastos del año (la calculadora usa las reglas de EE. UU. — región "United States").</p>
            <TaxCalculator initialRegion="US" embedded lang="es" />
          </div>
        </section>
      )}

      {data.kind === 'scanner' && (
        <section className="max-w-2xl mx-auto px-4 sm:px-8 pb-8">
          <TryScanner format={data.format} lang="es" />
        </section>
      )}

      {data.steps && (
        <section className="max-w-4xl mx-auto px-4 sm:px-8 py-8">
          <h2 className="text-3xl font-bold text-[#064E3B] text-center mb-8">Cómo funciona</h2>
          <div className="space-y-3 max-w-2xl mx-auto">
            {data.steps.map((s, i) => (
              <div key={i} className="flex items-start gap-3 bg-white rounded-2xl shadow-sm p-4" data-testid={`es-step-${i}`}>
                <span className="w-7 h-7 rounded-full bg-[#84CC16] text-black text-sm font-bold flex items-center justify-center flex-shrink-0">{i + 1}</span>
                <p className="text-gray-700 text-sm pt-0.5">{s}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="max-w-3xl mx-auto px-4 sm:px-8 py-8">
        <h2 className="text-2xl font-bold text-[#064E3B] text-center mb-8">Preguntas frecuentes</h2>
        <div className="space-y-4">
          {data.faqs.map((f, i) => (
            <div key={i} className="bg-white rounded-2xl shadow-sm p-6" data-testid={`es-faq-${i}`}>
              <h3 className="font-bold text-[#064E3B] mb-2">{f.q}</h3>
              <p className="text-gray-600 text-sm">{f.a}</p>
            </div>
          ))}
        </div>
        {(data.kind === 'calculator' || data.kind === 'guide') && (
          <p className="text-xs text-gray-400 text-center mt-6">Cifras de referencia para 2026. Es un estimado con fines de planeación, no asesoría fiscal. Consulta las reglas vigentes del IRS o a un contador para tu caso.</p>
        )}
      </section>

      <section className="max-w-3xl mx-auto px-4 sm:px-8 pb-4">
        <div className="bg-[#064E3B] rounded-3xl p-8 text-center">
          <h2 className="text-2xl font-bold text-white mb-2">Lleva tus cuentas listas para impuestos — desde WhatsApp</h2>
          <p className="text-[#A7F3D0] text-sm mb-5 max-w-lg mx-auto">Escribe tus ingresos y gastos, toma foto de tus recibos y MoneyFeed mantiene tus libros ordenados y listos para tu declaración. En español.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to={data.ctaHref}>
              <Button className="bg-[#84CC16] hover:bg-[#65A30D] text-black font-bold rounded-full px-7 py-6 text-base" data-testid="es-cta">
                {data.ctaLabel} <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </Link>
            <Link to="/auth">
              <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 font-bold rounded-full px-7 py-6 text-base" data-testid="es-signup">Crear cuenta gratis</Button>
            </Link>
          </div>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-8 py-8">
        <h2 className="text-lg font-bold text-[#064E3B] mb-4 text-center">Más herramientas en español</h2>
        <div className="flex flex-wrap justify-center gap-2">
          {others.map((o) => (
            <Link key={o.slug} to={`/es/${o.slug}`} className="px-4 py-2 bg-white rounded-full text-sm font-semibold text-[#064E3B] shadow-sm border border-gray-200 hover:border-[#84CC16]" data-testid={`es-link-${o.slug}`}>
              {o.badge}
            </Link>
          ))}
        </div>
      </section>

      <RelatedLinks />

      <footer className="max-w-6xl mx-auto px-4 sm:px-8 py-8 text-center">
        <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-500">
          <Link to="/" className="hover:text-[#064E3B]">Inicio</Link>
          <Link to="/es/contabilidad-por-whatsapp" className="hover:text-[#064E3B]">Contabilidad por WhatsApp</Link>
          <Link to="/es/calculadora-impuestos-1099" className="hover:text-[#064E3B]">Calculadora 1099</Link>
          <Link to="/auth" className="hover:text-[#064E3B]">Iniciar sesión</Link>
        </div>
      </footer>
    </div>
  );
}
